import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search as SearchIcon, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LOGO } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/api";
import { cn } from "@/lib/utils";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="logo-home" aria-label="MENAGroupFX home">
      <img src={LOGO} alt="MENAGroupFX" className="h-16 w-auto lg:h-24" />
    </Link>
  );
}

function LangToggle({ className }) {
  const { lang, setLang, L } = useLang();
  return (
    <button
      onClick={() => setLang(lang === "en" ? "ar" : "en")}
      className={cn("flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-ink/80 transition-colors hover:text-finblue", className)}
      data-testid={className && className.includes("mobile") ? "lang-toggle-mobile" : "lang-toggle"}
      aria-label="Switch language"
    >
      <Globe className="h-4 w-4" />
      {L.langName}
    </button>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { L } = useLang();

  const NAV = [
    { key: "markets", label: L.nav.markets, to: "/markets", links: [
      { label: L.nav.sub.forex[0], desc: L.nav.sub.forex[1], to: "/markets/forex" },
      { label: L.nav.sub.commodities[0], desc: L.nav.sub.commodities[1], to: "/markets/commodities" },
      { label: L.nav.sub.indices[0], desc: L.nav.sub.indices[1], to: "/markets/indices" },
      { label: L.nav.sub.shares[0], desc: L.nav.sub.shares[1], to: "/markets/shares" },
    ]},
    { key: "trading", label: L.nav.trading, to: "/trading-platforms", links: [
      { label: L.nav.sub.platforms[0], desc: L.nav.sub.platforms[1], to: "/trading-platforms" },
      { label: L.nav.sub.accounts[0], desc: L.nav.sub.accounts[1], to: "/account-solutions" },
      { label: L.nav.sub.how[0], desc: L.nav.sub.how[1], to: "/#how-it-works" },
    ]},
    { key: "insights", label: L.nav.insights, to: "/market-analysis", links: [
      { label: L.nav.sub.analysis[0], desc: L.nav.sub.analysis[1], to: "/market-analysis" },
      { label: L.nav.sub.calendar[0], desc: L.nav.sub.calendar[1], to: "/economic-calendar" },
      { label: L.nav.sub.education[0], desc: L.nav.sub.education[1], to: "/education" },
    ]},
    { key: "partners", label: L.nav.partners, to: "/partners", links: [
      { label: L.nav.sub.brokerPartners[0], desc: L.nav.sub.brokerPartners[1], to: "/broker-partners" },
      { label: L.nav.sub.become[0], desc: L.nav.sub.become[1], to: "/become-a-partner" },
      { label: L.nav.sub.ib[0], desc: L.nav.sub.ib[1], to: "/partners" },
    ]},
    { key: "about", label: L.nav.about, to: "/about-us" },
    { key: "contact", label: L.nav.contact, to: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpen(null); }, [pathname]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled ? "border-b border-hair bg-white/95 backdrop-blur shadow-sm" : "border-b border-transparent bg-white"
      )}
      data-testid="site-header"
    >
      <div className="container-tight flex h-20 items-center justify-between gap-4 lg:h-[104px]">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setOpen(null)}>
          {NAV.map((item) => (
            <div key={item.key} className="relative" onMouseEnter={() => setOpen(item.links ? item.key : null)}>
              <Link
                to={item.to}
                className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-ink/80 transition-colors hover:text-finblue"
                data-testid={`nav-${item.key}`}
              >
                {item.label}
                {item.links && <ChevronDown className="h-3.5 w-3.5" />}
              </Link>
              {item.links && open === item.key && (
                <div className="absolute start-0 top-full w-72 pt-2">
                  <div className="rounded-xl border border-hair bg-white p-2 shadow-lg">
                    {item.links.map((l) => (
                      <Link key={l.to} to={l.to} className="block rounded-lg px-3 py-2.5 text-start transition-colors hover:bg-canvas">
                        <span className="block text-sm font-semibold text-ink">{l.label}</span>
                        <span className="block text-xs text-subtle">{l.desc}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden items-center gap-1 lg:flex">
          <LangToggle />
          <Link to="/search" className="rounded-md p-2 text-ink/70 hover:text-finblue" data-testid="nav-search"><SearchIcon className="h-5 w-5" /></Link>
          <Button asChild variant="ghost" size="sm" className="text-ink/80" data-testid="nav-partner-login">
            <Link to="/admin/login">{L.common.partnerLogin}</Link>
          </Button>
          <Button asChild size="sm" className="bg-finblue hover:bg-finblueDark" onClick={() => trackEvent("open_account_click", { location: "header" })} data-testid="nav-open-account">
            <Link to="/broker-partners">{L.common.openAccount}</Link>
          </Button>
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <LangToggle className="mobile" />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="rounded-md p-2 text-ink" data-testid="mobile-menu-trigger" aria-label="Open menu"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto p-0">
              <div className="flex items-center justify-between border-b border-hair p-4">
                <Logo />
                <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X className="h-5 w-5 text-ink" /></button>
              </div>
              <div className="p-4">
                {NAV.map((item) => (
                  <div key={item.key} className="border-b border-hair py-1">
                    <Link to={item.to} className="block py-2.5 font-heading text-base font-semibold text-ink" data-testid={`mobile-nav-${item.key}`}>
                      {item.label}
                    </Link>
                    {item.links && (
                      <div className="pb-2 ps-3">
                        {item.links.map((l) => (
                          <Link key={l.to} to={l.to} className="block py-1.5 text-sm text-subtle hover:text-finblue">{l.label}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="mt-5 space-y-3">
                  <Button asChild className="w-full bg-finblue hover:bg-finblueDark" data-testid="mobile-open-account"><Link to="/broker-partners">{L.common.openAccount}</Link></Button>
                  <Button asChild variant="outline" className="w-full"><Link to="/admin/login">{L.common.partnerLogin}</Link></Button>
                  <Button asChild variant="ghost" className="w-full"><Link to="/search">{L.common.search}</Link></Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
